import { useState } from 'react';
import { OrbitMark } from '../hub/OrbitModuleHeader.jsx';
import { T } from '../styles/tokens.js';
import { useAuth } from './AuthContext.jsx';
import { ssoEnabled } from './msal.js';
import { API_BASE } from '../http/apiBase.js';

function getRedirect() {
  const p = new URLSearchParams(window.location.search).get('redirect');
  return p && p.startsWith('/') ? p : '/';
}

// Third-party brand mark — the one place literal hex is permitted.
function MicrosoftMark() {
  return (
    <svg width="15" height="15" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1"  y="1"  width="9" height="9" fill="#F25022" />
      <rect x="11" y="1"  width="9" height="9" fill="#7FBA00" />
      <rect x="1"  y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

// A redirect-flow SSO sign-in that verified at Microsoft but failed the Orbit exchange
// (typically: no account) lands back here after a full page reload — AuthContext parks the
// message because there was no login screen to show it on at the time. Read-and-clear so
// it shows exactly once.
function takeParkedSsoError() {
  try {
    const msg = sessionStorage.getItem('mp_sso_error') || '';
    sessionStorage.removeItem('mp_sso_error');
    return msg;
  } catch { return ''; }
}

export default function LoginPage() {
  const { setUser, loginSso } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState(takeParkedSsoError);
  const [busy,     setBusy]     = useState(false);

  function enter() {
    history.pushState(null, '', getRedirect());
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  async function handleSso() {
    setError('');
    setBusy(true);
    try {
      await loginSso();
      enter();
    } catch (err) {
      // Closing the Microsoft popup is a decision, not a failure — say nothing.
      if (err?.errorCode !== 'user_cancelled') {
        setError(err.message || 'Microsoft sign-in failed');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/portal/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ username: username.trim(), password }),
      });
      let data = {};
      try { data = await r.json(); } catch {}

      if (!r.ok) {
        setError(r.status >= 500
          ? 'Sign-in is temporarily unavailable. Try again shortly.'
          : data.error || (r.status === 401
            ? 'Invalid username or password'
            : 'Sign-in failed. Try again.'));
        return;
      }

      localStorage.setItem('mp_token', data.token);
      setUser(data.user);
      enter();
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={s.bg}>
      <div className="o-panel" style={s.card}>
        <div style={s.logo}>
          {/* On the white login card, not the ink top bar. */}
          <OrbitMark size={30} onInk={false} />
          <span style={s.logoText}>Orbit</span>
        </div>

        <h1 className="o-title" style={s.heading}>Welcome back</h1>
        <p className="o-sub" style={s.sub}>Sign in to your account to continue</p>

        <form onSubmit={handleSubmit} style={s.form}>
          <div style={s.field}>
            <label className="o-field-lbl" htmlFor="lg-user">Username or Email</label>
            <input
              className="o-control"
              id="lg-user"
              type="text"
              autoComplete="username"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="Enter your username or email"
              required
            />
          </div>
          <div style={s.field}>
            <label className="o-field-lbl" htmlFor="lg-pass">Password</label>
            <input
              className="o-control"
              id="lg-pass"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>

          {error && <div style={s.error}>{error}</div>}

          <button className="o-btn o-btn-primary" style={s.btn} type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {ssoEnabled && (
          <>
            <div style={s.divider} aria-hidden="true">
              <span style={s.dividerRule} />
              <span style={s.dividerText}>or</span>
              <span style={s.dividerRule} />
            </div>
            <button className="o-btn" style={{ ...s.btn, width: '100%' }} type="button"
                    onClick={handleSso} disabled={busy}>
              <MicrosoftMark />
              Sign in with Microsoft
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  bg: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: T.paper,
    padding: T.s5,
  },
  card: {
    padding: `${T.s7}px ${T.s6}px`,
    width: '100%',
    maxWidth: '400px',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: T.s6,
  },
  logoText: {
    fontFamily: T.fontDisplay,
    fontSize: '1.0625rem',
    fontWeight: 600,
    letterSpacing: '-.018em',
    color: T.text,
  },
  heading: {
    fontSize: '1.5rem',
    marginBottom: T.s1,
  },
  sub: {
    marginBottom: T.s5,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: T.s4,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
  },
  error: {
    background: T.errorWash,
    border: `1px solid ${T.error}33`,
    borderRadius: T.rSm,
    color: T.error,
    fontSize: T.tSmall,
    padding: `10px ${T.s3}px`,
  },
  btn: {
    marginTop: T.s1,
    justifyContent: 'center',
    padding: '10px',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: T.s3,
    margin: `${T.s4}px 0 ${T.s3}px`,
  },
  dividerRule: {
    flex: 1,
    height: '1px',
    background: T.rule,
  },
  dividerText: {
    color: T.text3,
    fontSize: T.tLabel,
    textTransform: 'uppercase',
    letterSpacing: '.08em',
  },
};
